/**
 * org-lifecycle.js — the pure logic behind what a customer sees while their
 * ACTIVE organisation moves through the tenant lifecycle
 * (`active → past_due → locked → scheduled_for_deletion → deleted`).
 * No `@/` imports, so `node --test tests/unit/` covers it
 * (tests/unit/org-lifecycle.test.mjs).
 *
 * Contract (auth-api 1.6.0 / cluster-api 2.73.0, built in parallel — mocked in
 * the tests; see docs/CUSTOMER_LIFECYCLE_STATES.md):
 *   GET /api/auth/me — every org block carries
 *     lifecycle_state, locked_at, deletion_due_at,
 *     lifecycle: {past_due_days, locked_days, retention_days}
 *   and the top level carries `account_locked` for a personal lock.
 *   cluster-api refuses mutating customer calls of a locked org with
 *     423 {error|code: "organization_locked"}.
 *   GET /api/cluster/tenants/orgs/<org_id>/exports → export status + download
 *   GET /api/cluster/tenants/orgs/<org_id>/exports/<project_id>/download
 *
 * Only the ACTIVE organisation decides: a user who belongs to several orgs is
 * restricted only while the locked one is active, and the org switcher stays.
 *
 * Author: Matew Makhatadze
 */

export const LIFECYCLE_STATES = Object.freeze([
  'active', 'past_due', 'locked', 'scheduled_for_deletion', 'deleted'
])

/** Plan defaults when an org block carries no `lifecycle` durations. */
export const DEFAULT_DURATIONS = Object.freeze({ past_due_days: 7, locked_days: 14, retention_days: 30 })

/** The page every restricted route lands on. */
export const LOCKED_PATH = '/my/organisation-locked'

/**
 * Routes a locked organisation keeps: Billing, the locked page (which holds
 * the data export) and the personal profile. Everything else under `/my/`
 * and the customer dashboard (`/admin`, `/admin/members`) redirects.
 */
export const LOCKED_ALLOWED_PREFIXES = Object.freeze([LOCKED_PATH, '/my/billing', '/changeprofile'])

const DAY_MS = 24 * 60 * 60 * 1000

/* ───────────────────────────── org + state ───────────────────────────── */

/**
 * The active organisation's block from the /me-shaped user: the org flagged
 * `is_current` in `organizations`, else `current_organization`.
 */
export function activeOrgOf(user) {
  if (!user || typeof user !== 'object') return null
  const list = Array.isArray(user.organizations) ? user.organizations : []
  const current = list.find((o) => o && o.is_current) || null
  const block = user.current_organization && typeof user.current_organization === 'object'
    ? user.current_organization
    : null
  if (current && block && String(current.id) === String(block.id)) {
    // Either source may be the one /me filled with lifecycle fields.
    return { ...current, ...pickDefined(block) }
  }
  return current || block
}

function pickDefined(obj) {
  const out = {}
  for (const [k, v] of Object.entries(obj || {})) if (v !== undefined && v !== null) out[k] = v
  return out
}

/** Normalised lifecycle state of an org block; unknown/missing → 'active'. */
export function lifecycleStateOf(org) {
  const raw = String(org?.lifecycle_state || '').trim().toLowerCase()
  return LIFECYCLE_STATES.includes(raw) ? raw : 'active'
}

/** System orgs never leave `active` (contract) — guard the client too. */
export function effectiveStateOf(org) {
  if (!org || org.is_system_org === true) return 'active'
  return lifecycleStateOf(org)
}

export function isRestrictedState(state) {
  return state === 'locked' || state === 'scheduled_for_deletion' || state === 'deleted'
}

/** True when the ACTIVE org of this user is locked / scheduled for deletion. */
export function isActiveOrgRestricted(user) {
  return isRestrictedState(effectiveStateOf(activeOrgOf(user)))
}

export function durationsOf(org) {
  const l = org?.lifecycle && typeof org.lifecycle === 'object' ? org.lifecycle : {}
  const num = (v, d) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : d)
  return {
    past_due_days: num(l.past_due_days, DEFAULT_DURATIONS.past_due_days),
    locked_days: num(l.locked_days, DEFAULT_DURATIONS.locked_days),
    retention_days: num(l.retention_days, DEFAULT_DURATIONS.retention_days)
  }
}

function toDate(v) {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * When a past-due org will be locked. Prefers an explicit `lock_due_at`;
 * otherwise `past_due_at` (or `past_due_since`) + `past_due_days`. Null when
 * /me gives no anchor — the banner then says "soon" instead of a date.
 */
export function lockDueAtOf(org) {
  const explicit = toDate(org?.lock_due_at)
  if (explicit) return explicit
  const since = toDate(org?.past_due_at || org?.past_due_since)
  if (!since) return null
  return new Date(since.getTime() + durationsOf(org).past_due_days * DAY_MS)
}

/**
 * When the org's data will be deleted. `deletion_due_at` when /me has set it
 * (scheduled_for_deletion); otherwise, for a locked org,
 * `locked_at + locked_days` plus the one-day final-notice grace.
 */
export function deletionDueAtOf(org) {
  const explicit = toDate(org?.deletion_due_at)
  if (explicit) return explicit
  const lockedAt = toDate(org?.locked_at)
  if (!lockedAt) return null
  return new Date(lockedAt.getTime() + (durationsOf(org).locked_days + 1) * DAY_MS)
}

/* ───────────────────────────── formatting ───────────────────────────── */

/** "23 October 2026" — British day-month-year, fixed to Asia/Tbilisi. */
export function formatLifecycleDate(date) {
  const d = toDate(date)
  if (!d) return ''
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Tbilisi'
    }).format(d)
  } catch {
    return d.toISOString().slice(0, 10)
  }
}

/**
 * Countdown parts to `due` from `now`: {days, hours, overdue}. Rounded down;
 * a past due date reports overdue with zeros.
 */
export function countdownTo(due, now = new Date()) {
  const d = toDate(due)
  if (!d) return null
  const ms = d.getTime() - toDate(now).getTime()
  if (ms <= 0) return { days: 0, hours: 0, overdue: true }
  const days = Math.floor(ms / DAY_MS)
  const hours = Math.floor((ms - days * DAY_MS) / (60 * 60 * 1000))
  return { days, hours, overdue: false }
}

/** "3 days" / "1 day, 4 hours" / "less than an hour". */
export function countdownLabel(parts) {
  if (!parts) return ''
  if (parts.overdue) return 'any moment now'
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`
  if (parts.days > 0) {
    return parts.hours > 0 && parts.days < 3
      ? `${plural(parts.days, 'day')}, ${plural(parts.hours, 'hour')}`
      : plural(parts.days, 'day')
  }
  return parts.hours > 0 ? plural(parts.hours, 'hour') : 'less than an hour'
}

/* ───────────────────────────── banner ───────────────────────────── */

/**
 * The persistent past-due banner, or null when the active org is not past due.
 * → {text, linkTo, linkLabel, dueAt}
 */
export function pastDueBanner(user) {
  const org = activeOrgOf(user)
  if (effectiveStateOf(org) !== 'past_due') return null
  const due = lockDueAtOf(org)
  const when = due ? `on ${formatLifecycleDate(due)}` : 'soon'
  return {
    text: `Payment overdue — your organisation will be locked ${when}.`,
    linkTo: '/my/billing',
    linkLabel: 'Pay now',
    dueAt: due ? due.toISOString() : null,
    orgName: org?.name || null
  }
}

/** Banner surfaces: every `/my/*` page and the customer dashboard. */
export function isBannerRoute(path) {
  const p = String(path || '')
  return p === '/admin' || p.startsWith('/admin/') || p.startsWith('/my/')
}

/* ───────────────────────────── routing ───────────────────────────── */

/** A route the lock restricts: every `/my/*`, `/admin` and `/admin/*`. */
export function isLockGatedPath(path) {
  const p = String(path || '')
  return p === '/my' || p.startsWith('/my/') || p === '/admin' || p.startsWith('/admin/') ||
    /^\/marketplace\/[^/]+\/deploy\/?$/.test(p)
}

export function isAllowedWhileLocked(path) {
  const p = String(path || '')
  return LOCKED_ALLOWED_PREFIXES.some((a) => p === a || p.startsWith(`${a}/`) || p.startsWith(`${a}?`))
}

/**
 * Router decision: null to continue, or the redirect target.
 *   - restricted active org + gated, not-allowed path → the locked page
 *   - unrestricted active org on the locked page → /my/apps (nothing to show)
 * Operators keep their console (`/admin/environments*`) whatever their org.
 */
export function lockedRedirectFor(path, user) {
  const p = String(path || '')
  const restricted = isActiveOrgRestricted(user)
  const operator = user?.is_super_admin === true || user?.is_admin === true
  if (restricted) {
    if (operator && p.startsWith('/admin/environments')) return null
    if (isLockGatedPath(p) && !isAllowedWhileLocked(p)) return LOCKED_PATH
    return null
  }
  // An org we have no lifecycle data for yet stays put; only an org we KNOW
  // is fine leaves the locked page.
  if (p === LOCKED_PATH && activeOrgOf(user)) return '/my/apps'
  return null
}

/**
 * The navigation while locked: keep only Billing, Data export (the locked
 * page) and Profile; Sign out is rendered separately and always stays.
 * `items` are `{to, …}`.
 */
export function filterNavForLock(items, user) {
  if (!isActiveOrgRestricted(user)) return items
  return (items || []).filter((i) => i && isAllowedWhileLocked(i.to))
}

/** Primary nav links while locked: the public pages only, no app surfaces. */
export function filterPrimaryLinksForLock(links, user) {
  if (!isActiveOrgRestricted(user)) return links
  return (links || []).filter((l) => l && (l.external || !isLockGatedPath(l.to)))
}

/* ───────────────────────────── locked page ───────────────────────────── */

/**
 * View-model for the locked page.
 * → {state, orgName, reason, lockedAt, deletionDueAt, countdown, countdownText,
 *    headline, lead, retentionDays, canPay, otherOrgs}
 */
export function lockedPageModel(user, now = new Date()) {
  const org = activeOrgOf(user)
  const state = effectiveStateOf(org)
  const deletionDue = deletionDueAtOf(org)
  const countdown = deletionDue ? countdownTo(deletionDue, now) : null
  const reason = String(org?.locked_reason || org?.lifecycle_reason || '').trim() ||
    (state === 'locked' || state === 'scheduled_for_deletion'
      ? 'Payment for this organisation is overdue.'
      : '')
  const name = org?.name || 'Your organisation'
  let headline = 'Your organisation is locked'
  let lead = `${name} is locked. Your apps are stopped and your data is kept. Pay the overdue invoice to restore everything as it was.`
  if (state === 'scheduled_for_deletion') {
    headline = 'Your organisation is scheduled for deletion'
    lead = `${name} will be deleted ${deletionDue ? `on ${formatLifecycleDate(deletionDue)}` : 'soon'}. Pay now to stop the deletion, or download your code before then.`
  } else if (state === 'deleted') {
    headline = 'Your organisation has been deleted'
    lead = `${name} was deleted. Your user account is unaffected.`
  }
  const others = (Array.isArray(user?.organizations) ? user.organizations : [])
    .filter((o) => o && !o.is_current && (!org || String(o.id) !== String(org.id)))
    .map((o) => ({ id: o.id, name: o.name, state: effectiveStateOf(o) }))
  return {
    state,
    orgId: org?.id ?? null,
    orgName: name,
    reason,
    lockedAt: toDate(org?.locked_at),
    deletionDueAt: deletionDue,
    deletionDateText: deletionDue ? formatLifecycleDate(deletionDue) : '',
    countdown,
    countdownText: countdownLabel(countdown),
    headline,
    lead,
    retentionDays: durationsOf(org).retention_days,
    // Billing access follows the existing per-org grant; an explicit false
    // means "ask your owner" rather than a dead Pay now link.
    canPay: org ? org.can_view_billing !== false : true,
    otherOrgs: others
  }
}

/* ───────────────────────────── exports ───────────────────────────── */

/**
 * Normalise the exports listing into rows
 * {projectId, name, status, ready, size, expiresAt, error}. Tolerates
 * `{exports: [...]}`, `{projects: [...]}` or a bare array.
 */
export function normalizeExports(payload) {
  let list = []
  if (Array.isArray(payload)) list = payload
  else if (Array.isArray(payload?.exports)) list = payload.exports
  else if (Array.isArray(payload?.projects)) list = payload.projects
  else if (Array.isArray(payload?.items)) list = payload.items
  return list.filter(Boolean).map((e) => {
    const projectId = e.project_id ?? e.id ?? e.projectId ?? null
    const status = String(e.status || e.export_status || (e.download_url ? 'finished' : 'unknown')).toLowerCase()
    const ready = ['finished', 'ready', 'done', 'succeeded', 'completed'].includes(status)
    return {
      projectId,
      name: e.name || e.path_with_namespace || e.project_path || e.path || (projectId !== null && projectId !== undefined ? `Project ${projectId}` : 'Project'),
      status,
      ready,
      size: Number.isFinite(Number(e.size_bytes)) ? Number(e.size_bytes) : null,
      expiresAt: toDate(e.expires_at),
      error: e.error || null
    }
  })
}

export function exportStatusLabel(row) {
  if (!row) return ''
  if (row.ready) return 'Ready to download'
  if (['failed', 'error'].includes(row.status)) return 'Export failed'
  if (['queued', 'started', 'regeneration_in_progress', 'pending', 'running', 'none'].includes(row.status)) return 'Preparing'
  return 'Preparing'
}

export function formatBytes(n) {
  if (!Number.isFinite(n) || n < 0) return ''
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`
}

/** A quiet line for a failed exports load (403 → owners/admins only). */
export function exportsFailureNote(err) {
  const s = err?.response?.status ?? err?.status
  if (s === 403) return 'Only the organisation owner and admins can download the code export.'
  if (s === 404) return 'No code export has been prepared for this organisation.'
  return 'The export list could not be loaded. Try again in a moment.'
}

/* ───────────────────────────── 423 handling ───────────────────────────── */

/** True for cluster-api's `423 organization_locked` refusal. */
export function isOrganizationLockedError(err) {
  const r = err?.response
  if (!r || r.status !== 423) return false
  const d = r.data
  const codes = [d?.error, d?.code, d?.error_code, d?.error?.code, d?.data?.error, d?.data?.code]
    .filter((c) => typeof c === 'string')
  // Any 423 from the platform means the org is locked; a body naming another
  // code (should one appear) is not ours to reroute.
  return codes.length === 0 || codes.includes('organization_locked')
}

/**
 * One redirect per burst: the first 423 navigates to the locked page and
 * refreshes /me; further 423s within `windowMs`, or while already on the
 * locked page, do nothing. `deps`: {navigate(path), refresh(), currentPath(),
 * now()}.
 */
export function createLockedRedirector(deps, { windowMs = 30000 } = {}) {
  let lastAt = -Infinity
  let pending = false
  return function onLocked() {
    const now = deps.now ? deps.now() : Date.now()
    if (pending) return false
    if (now - lastAt < windowMs) return false
    const here = deps.currentPath ? deps.currentPath() : ''
    lastAt = now
    try { deps.refresh && deps.refresh() } catch { /* best effort */ }
    if (here === LOCKED_PATH) return false
    pending = true
    Promise.resolve()
      .then(() => deps.navigate(LOCKED_PATH))
      .catch(() => {})
      .finally(() => { pending = false })
    return true
  }
}
